import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReactNode } from "react";
import type { View, WeekYear } from "@chef/types/common";
import type { StandaloneBasketVariant } from "@chef/types/basket";
import {
  OrdersQuery,
  PickAndMixQuery,
  TimeblocksByRangeWithGeoQuery,
  TimeblocksQuery,
} from "../graphql/generated";
import { IDeviationProduct, RootState } from "../";
export type StandaloneProductModalParams = {
  id: "STANDALONE_PRODUCT";
  productId: string;
  variationId: string;
  week: number;
  year: number;
  view: View;
  basketType?: StandaloneBasketVariant;
};

type DrawerOverlayParams = {
  open: boolean;
};

export type RecipeModalParams = {
  id: "RECIPE";
  recipeId: number;
  pickAndMixProduct?: PickAndMixQuery["pickAndMix"][0] | null;
  misc?: {
    portionSize?: string;
    updateProduct?: {
      productId: string;
      variationId: string;
      mainRecipeId: number | null;
      recipeId: number;
      recipeName: string;
      price: number;
      sortingPosition: number;
      recommendationScore: number;
    };
  } | null;
  view?: "historic" | "deviation" | "conversion";
};

export type SubscribedStandaloneModalParams = WeekYear & {
  id: "SUBSCRIBED_STANDALONE";
  deviationProduct: IDeviationProduct | null;
  productName: string | null;
  maxQtyAllowed: number | null;
};

export type WeekSchedulerEditorModalParams = WeekYear & {
  id: "WEEK_SCHEDULER_EDITOR";
};

type OverrideFeatureFlagsModalParams = {
  id: "OVERRIDE_FEATURE_FLAGS";
};

export type ResumeOngoingDeviationModalParams = WeekYear & {
  id: "RESUME_ONGOING_DEVIATION";
  deviationProducts: IDeviationProduct[];
  calendarBasketProducts: IDeviationProduct[];
  destinationUrl: string;
};

export type PersonalisedRecipesModalParams = WeekYear & {
  id: "PERSONALISED_RECIPES";
  conceptPreferenceIds: string[];
  tastePreferenceIds: string[];
  portions: number;
};

export type WeekPortionModalParams = WeekYear & {
  id: "WEEK_PORTION";
} & WeekYear;

export type ConfirmRevertModalProps = {
  id: "CONFIRM_REVERT";
} & WeekYear;

export type DeviationCartModalParams = WeekYear & {
  id: "DEVIATION_CART";
};

export type SubscriptionCartModalParams = WeekYear & {
  id: "SUBSCRIPTION_CART";
};

export type CampaignCartModalParams = WeekYear & {
  id: "CAMPAIGN_CART";
  promotionIdentity: {
    icon: string;
    indefiniteName: string;
  };
};

export type FilterNavigationModalParams = {
  id: "FILTER_NAVIGATION";
  items: {
    value: string;
    label: string;
    count: number;
    type: "default" | "highlighted" | "secondary";
    isAll: boolean;
  }[];
  onClick: (value: string) => void;
  values: string[];
};

type ConsentModalParams = {
  id: "CONSENT";
};

export type AccountReactivationModalParams = {
  id: "ACCOUNT_REACTIVATION";
  redirectUrl: string;
  schedulerUpdates?: {
    week: number;
    year: number;
    delivery: boolean;
  }[];
};

export type CancelSubscriptionModalParams = {
  id: "CANCEL_SUBSCRIPTION";
  onConfirm: () => void;
  discounts: string[];
  pausePageUrl: string;
};

type RecipeActionModalParams = {
  id: "RECIPE_ACTION";
};

export type PreselectedRecipesModalParams = {
  id: "PRESELECTED_RECIPES";
  preferenceIds: string[];
  portions: number;
};

type RenewVippsAgreementModalParams = {
  id: "RENEW_VIPPS_AGREEMENT";
};

export type AddNewAddressModalParams = {
  id: "ADD_NEW_ADDRESS";
  setSelectedAddressId: (addressId: string) => void;
};

export type ShopItemModalParams = {
  id: "SHOP_ITEM";
  variationId: string;
  name: string;
  productPointPrice: number;
};

export type AddressChangedAfterCutoffModalParams = {
  id: "ADDRESS_CHANGED_AFTER_CUTOFF";
  nextDeliveryDate: string;
  onConfirm: () => void;
};

type RaatassOptOutModalParams = {
  id: "RAATASS_OPT_OUT";
};

export type CustomerExistsModalParams = {
  id: "CUSTOMER_EXISTS";
  onClose?: () => void;
  initialLoginMethod: "email" | "telephone";
  initialTelephone?: string;
  initalEmail?: string;
};

export type FirstDeliveryInformationModalParams = {
  id: "FIRST_DELIVERY_INFORMATION";
  content?: unknown | null;
};

export type LeaveWithoutSavingModalParams = {
  id: "LEAVE_WITHOUT_SAVING";
  onClose: () => void;
  onConfirm: () => void;
};

type ConsentsWarningModalParams = {
  id: "CONSENTS_WARNING";
};

export type DeliveryTimeAdjustedModalParams = {
  id: "DELIVERY_TIME_ADJUSTED";
  BlockContent: () => JSX.Element | null;
};

export type CampaignOrderPauseModalParams = {
  id: "CAMPAIGN_ORDER_PAUSE";
  campaignMealboxName: string;
  deliveryCutOffDate: string;
  campaignOrder: OrdersQuery["orders"][number];
  promotionSlug: string;
};

type RemoveAddressModalParams = {
  id: "REMOVE_ADDRESS";
};

type SubscriptionCancelModalParams = {
  id: "SUBSCRIPTION_CANCEL";
};

export type ChangeFirstDeliveryDateModalParams = {
  id: "CHANGE_FIRST_DELIVERY_DATE";
  availableWeekAndYearTimeblocks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"];
  availableTimeblocks: TimeblocksQuery["timeblocksData"]["timeblocks"];
};

export type BlockUserInteractionModalParams = {
  id: "BLOCK_USER_INTERACTION";
  children: ReactNode;
};

export type CheckoutErrorsModalParams = {
  id: "CHECKOUT_ERRORS";
  onClose: () => void;
};

export type PreselectorLoaderModalParams = {
  id: "PRESELECTOR_LOADER";
  stage: number;
};

export type TryNewAppModalParams = {
  id: "TRY_NEW_APP";
};

export type RatingAndCommentModalParams = {
  id: "RATING_AND_COMMENT";
  ratingData: {
    rating?: number;
    comment?: string;
    quickCommentIds?: string[];
  };
  recipeId: number;
  recipeName: string;
};

export type ModalData =
  | RecipeModalParams
  | StandaloneProductModalParams
  | SubscribedStandaloneModalParams
  | WeekSchedulerEditorModalParams
  | OverrideFeatureFlagsModalParams
  | ResumeOngoingDeviationModalParams
  | PersonalisedRecipesModalParams
  | WeekPortionModalParams
  | FilterNavigationModalParams
  | ConsentModalParams
  | AccountReactivationModalParams
  | CancelSubscriptionModalParams
  | RecipeActionModalParams
  | PreselectedRecipesModalParams
  | RenewVippsAgreementModalParams
  | DeviationCartModalParams
  | SubscriptionCartModalParams
  | CampaignCartModalParams
  | AddNewAddressModalParams
  | ShopItemModalParams
  | AddressChangedAfterCutoffModalParams
  | RaatassOptOutModalParams
  | CustomerExistsModalParams
  | FirstDeliveryInformationModalParams
  | LeaveWithoutSavingModalParams
  | ConsentsWarningModalParams
  | DeliveryTimeAdjustedModalParams
  | CampaignOrderPauseModalParams
  | RemoveAddressModalParams
  | SubscriptionCancelModalParams
  | ChangeFirstDeliveryDateModalParams
  | ConfirmRevertModalProps
  | BlockUserInteractionModalParams
  | CheckoutErrorsModalParams
  | PreselectorLoaderModalParams
  | RatingAndCommentModalParams
  | TryNewAppModalParams;

export type Modal =
  | {
      open: false;
      data: null;
    }
  | {
      open: true;
      data: ModalData;
    };

const initialState = {
  modal: {
    open: false,
    data: null,
  } as Modal,

  drawer: {
    open: false,
  } as DrawerOverlayParams,
};

export const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<ModalData>) => {
      state.modal = {
        open: true,
        data: action.payload,
      };
    },
    closeModal: (state) => {
      state.modal = {
        open: false,
        data: null,
      };
    },

    toggleDrawerModal: (state) => {
      state.drawer.open = !state.drawer.open;
    },

    hideDrawerModal: (state) => {
      state.drawer.open = false;
    },
  },
});

export const { openModal, closeModal, toggleDrawerModal, hideDrawerModal } =
  modalSlice.actions;

export const selectDrawerModalIsOpen = (state: RootState) =>
  state.modal.drawer.open;

export const selectModalIsOpen =
  (modalId: ModalData["id"]) =>
  (state: RootState): boolean => {
    const modalIsOpen = state.modal.modal.open;
    const modalHasId = state.modal.modal.data?.id === modalId;

    return modalIsOpen && modalHasId;
  };

export default modalSlice.reducer;
